import { Controller } from "@hotwired/stimulus"
import tippy from 'tippy.js'
import 'tippy.js/dist/tippy.css'

export default class extends Controller {
  static targets = ["avatar", "overflow"]
  static values = {
    maxVisible: { type: Number, default: 10 }
  }

  connect() {
    this.updateVisibleAvatars()
    
    // Add resize observer to recalculate on container width change
    this.resizeObserver = new ResizeObserver(() => {
      this.updateVisibleAvatars()
    })
    this.resizeObserver.observe(this.element)
    
    // Initialize Tippy tooltips
    this.initializeTooltips()
  }

  disconnect() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    
    // Dispose Tippy tooltips
    this.avatarTargets.forEach(avatar => {
      if (avatar._tippy) {
        avatar._tippy.destroy()
      }
    })
  }

  initializeTooltips() {
    // Initialize Tippy.js tooltips for all avatars
    this.avatarTargets.forEach(avatar => {
      const title = avatar.dataset.tippyContent
      if (title) {
        tippy(avatar, {
          content: title,
          placement: 'top',
          animation: 'scale',
          duration: [200, 0] // Show in 200ms, hide instantly
        })
      }
    })
  }

  updateVisibleAvatars() {
    if (!this.hasAvatarTarget) return

    const containerWidth = this.element.offsetWidth
    const avatarWidth = this.getAvatarWidth()
    const overlapPercent = 0.40 // 40% overlap
    const effectiveWidth = avatarWidth * (1 - overlapPercent)
    
    // Calculate how many avatars can fit
    // First avatar takes full width, rest take effectiveWidth
    const maxVisible = Math.floor((containerWidth - avatarWidth) / effectiveWidth) + 1
    
    const totalAvatars = this.avatarTargets.length
    const visibleCount = Math.min(maxVisible, totalAvatars)
    
    // Show/hide avatars based on calculation
    this.avatarTargets.forEach((avatar, index) => {
      if (index < visibleCount) {
        avatar.classList.remove('d-none')
      } else {
        avatar.classList.add('d-none')
      }
    })
    
    // Update overflow badge (+N)
    if (this.hasOverflowTarget) {
      const overflowCount = totalAvatars - visibleCount
      if (overflowCount > 0) {
        this.overflowTarget.textContent = `+${overflowCount}`
        this.overflowTarget.classList.remove('d-none')
      } else {
        this.overflowTarget.classList.add('d-none')
      }
    }
  }

  getAvatarWidth() {
    // Get the actual width of an avatar element
    if (this.hasAvatarTarget) {
      return this.avatarTargets[0].offsetWidth
    }
    return 40 // default fallback
  }

  onAvatarMouseEnter(event) {
    // Increase z-index on hover to bring avatar to front
    const avatar = event.currentTarget
    const currentZIndex = parseInt(avatar.dataset.zIndex || 0)
    avatar.style.zIndex = 1000 // Bring to front
    avatar.dataset.originalZIndex = currentZIndex
  }

  onAvatarMouseLeave(event) {
    // Restore original z-index
    const avatar = event.currentTarget
    const originalZIndex = avatar.dataset.originalZIndex || avatar.dataset.zIndex || 0
    avatar.style.zIndex = originalZIndex
  }
}

